import { create } from "zustand";
import type { ReactNode } from "react";
import { pushNotificationsAllowed } from "../api/pushContext.js";
import { onRequestScopeReset } from "../api/scope.js";
import { AssetIcon } from "../assets/Icon.js";

export interface ToastItem {
  id: number;
  kind: "info" | "success" | "error" | "reward";
  text: ReactNode;
  until: number;
}

interface ToastState {
  toasts: ToastItem[];
  push(kind: ToastItem["kind"], text: ReactNode, ttlMs?: number): void;
  dismiss(id: number): void;
}

let nextId = 1;
const timers = new Map<number, ReturnType<typeof setTimeout>>();

export const useToasts = create<ToastState>((set, get) => ({
  toasts: [],
  push: (kind, text, ttlMs = 3500) => {
    if (!pushNotificationsAllowed()) return;
    const id = nextId++;
    set({ toasts: [...get().toasts, { id, kind, text, until: Date.now() + ttlMs }] });
    timers.set(id, setTimeout(() => get().dismiss(id), ttlMs));
  },
  dismiss: (id) => { clearTimeout(timers.get(id)); timers.delete(id); set({ toasts: get().toasts.filter((t) => t.id !== id) }); },
}));
onRequestScopeReset(() => { timers.forEach(clearTimeout); timers.clear(); useToasts.setState({ toasts: [] }); });

export const toast = {
  info: (text: ReactNode) => useToasts.getState().push("info", text),
  success: (text: string) => useToasts.getState().push("success", text),
  error: (text: string) => useToasts.getState().push("error", text, 5000),
  reward: (text: string) => useToasts.getState().push("reward", text, 4000),
};

const COLORS: Record<ToastItem["kind"], string> = { info: "var(--accent)", success: "var(--success)", error: "var(--danger)", reward: "var(--gold)" };

export function ToastCard({ kind, children }: { kind: ToastItem["kind"]; children: ReactNode }) {
  return <div className={`glass fade-in toast-card ${kind}`} role={kind === "error" ? "alert" : "status"} style={{ padding: "8px 12px", borderLeft: `4px solid ${COLORS[kind]}`, maxWidth: "min(92vw, 420px)", fontSize: 13 }}>
    {kind === "reward" || kind === "success" ? <AssetIcon id={kind === "reward" ? "itm.chest_gold" : "ui.stat.star"} size={28} alt="" className="toast-emblem" /> : null}
    <span>{children}</span>
  </div>;
}

/** A burst of actions must not cover the next action. Errors retain a separate visible slot. */
export function visibleToasts(toasts: ToastItem[]): ToastItem[] {
  const error = toasts.findLast(t => t.kind === "error");
  const latest = toasts.findLast(t => t.kind !== "error");
  return [error, latest].filter((t): t is ToastItem => t !== undefined);
}

export function ToastHost() {
  const toasts = useToasts((s) => s.toasts);
  return (
    <div style={{ position: "fixed", top: "calc(var(--safe-top) + 64px)", left: 0, right: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, pointerEvents: "none", zIndex: 50 }}>
      {visibleToasts(toasts).map((t) => (
        <ToastCard key={t.id} kind={t.kind}>{t.text}</ToastCard>
      ))}
    </div>
  );
}
